import { WorkareaPricingData } from './workareaPricing'
import { useQueries, useQuery, useQueryClient } from 'react-query'
import { getSelectedWorkareaPricing, previewWorkareaPricing, WorkareaPricingPreviewRequest, WorkareaPricingSelection } from './workareaPricingApi'

const selectionKey = (jobId: number) => ['workarea-pricing-selection', jobId]
const previewKey = (jobId: number) => ['workarea-pricing-preview', jobId]

export const useSelectedWorkareaPricing = (jobId: number, selections: WorkareaPricingSelection[], enabled: boolean) =>
  useQuery([...selectionKey(jobId), selections], async () =>
    (await Promise.all(selections.map(selection => getSelectedWorkareaPricing(jobId, selection)))).flat(),
  { enabled, keepPreviousData: false })

export const useWorkareaPricingPreviews = (jobId: number, requests: { workareaId: number, request: WorkareaPricingPreviewRequest, pricing?: WorkareaPricingData | null, isApproved: boolean }[]) =>
  useQueries(requests.map(({ workareaId, request, pricing, isApproved }) => ({
    queryKey: [...previewKey(jobId), workareaId, request, pricing, isApproved],
    queryFn: () => previewWorkareaPricing(workareaId, request),
    staleTime: Infinity,
  })))

export const useVisitPricingSummaries = (jobId: number, visitIds: string[], enabled: boolean) => {
  return useQuery([...selectionKey(jobId), 'visit-summaries', visitIds], async () => {
    const visits = await Promise.all(visitIds.map(async visitId => {
      const selection = { visitIds: [visitId], includeNonVisitItems: false }
      const [all, uninvoiced] = await Promise.all([
        getSelectedWorkareaPricing(jobId, selection),
        getSelectedWorkareaPricing(jobId, { ...selection, uninvoicedOnly: true }),
      ])
      return { visitId, all, uninvoiced }
    }))
    const nonVisit = await getSelectedWorkareaPricing(jobId, { visitIds: [], uninvoicedOnly: true })
    return { visits, nonVisit }
  }, { enabled })
}

export const useWorkareaPricingInvalidator = () => {
  const client = useQueryClient()
  return {
    invalidateAll: () => client.invalidateQueries(['workarea-pricing-selection']),
    invalidateForJob: (jobId: number) => Promise.all([
      client.invalidateQueries(selectionKey(jobId)),
      client.invalidateQueries(previewKey(jobId)),
    ])
  }
}
